import {
  Dialog,
  FormInput,
  FormSelect,
  FormTextarea,
  Loader,
} from "@/components";
import { FC } from "react";
import { CopyBudgetProps } from "./types";
import { FormProvider } from "react-hook-form";
import useCopyBudget from "./useCopyBudget";
import { Button, DialogFooter } from "@/components/ui";
import { DialogClose } from "@radix-ui/react-dialog";
import { BUDGET_NAME, NOTES, VERSION_NO, YEAR } from "./consts";
import { yearOptions } from "@/lib/utils";

const CopyBudget: FC<CopyBudgetProps> = ({
  showCopyDialog,
  setShowCopyDialog,
  copyVersionDetails,
}) => {
  const { form, requiredFields, isCopyPending, onCloseDialog, onSubmit } =
    useCopyBudget({ setShowCopyDialog, copyVersionDetails });
  const { errors } = form.formState;

  return (
    <Dialog
      open={showCopyDialog}
      setOpen={setShowCopyDialog}
      title={
        <div className="text-2xl font-bold text-primary flex items-center gap-2">
          Copy Version
        </div>
      }
      hideFooter
      contentClassName="w-[1000px]"
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-col gap-2">
          <p className="text-lg font-bold">From</p>
          <p className="text-sm">{copyVersionDetails?.budgetName}</p>
          <p className="text-lg font-bold text-primary">
            Year {copyVersionDetails?.year}
          </p>
          <p className="text-sm">Version: {copyVersionDetails?.versionNo}</p>
        </div>
        <div className="flex flex-col gap-2 w-[60%] border-s-1 border-gray-200 ps-6 ms-6">
          <p>To</p>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-[30px]">
                <FormInput
                  name={BUDGET_NAME}
                  control={form.control}
                  label="Budget Name"
                  placeholder="Please enter"
                  error={errors[BUDGET_NAME]?.message}
                  isRequired={requiredFields.includes(BUDGET_NAME)}
                />
                <FormSelect
                  name={YEAR}
                  control={form.control}
                  label="Year"
                  placeholder="Please select"
                  error={errors[YEAR]?.message}
                  isRequired={requiredFields.includes(YEAR)}
                  options={yearOptions(5)}
                />
                <FormSelect
                  name={VERSION_NO}
                  control={form.control}
                  label="Version No"
                  placeholder="Please enter"
                  error={errors[VERSION_NO]?.message}
                  isRequired={requiredFields.includes(VERSION_NO)}
                  options={new Array(26).fill(0).map((_, index) => {
                    return {
                      label: index?.toString(),
                      value: index.toString(),
                    };
                  })}
                />
                <FormTextarea
                  name={NOTES}
                  control={form.control}
                  label="Notes"
                  placeholder="Please enter"
                  error={errors[NOTES]?.message}
                  isRequired={requiredFields.includes(NOTES)}
                />
              </div>
              <DialogFooter className="mt-5">
                <DialogClose>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isCopyPending}
                    onClick={onCloseDialog}
                  >
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={isCopyPending}>
                  {isCopyPending && <Loader variant="secondary" />} Copy
                </Button>
              </DialogFooter>
            </form>
          </FormProvider>
        </div>
      </div>
    </Dialog>
  );
};

export default CopyBudget;
